import L, { Marker as LeafletMarker } from 'leaflet';
import { delay } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import { Circle, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import './markercluster.d.ts';
import { SessionInfoTooltip } from './SessionInfoTooltip';
import { LeafletMapPoint } from './types';
import { checkCommaPosForCluster, processBigNumber } from './utils';

const MAX_CLUSTER_CHILDREN = 500;

type CustomMarkerClusterGroupProps = {
  id?: string;
  points: LeafletMapPoint[];
  circleRadius: number;
};

const markerIcon = L.icon({
  iconUrl: '/marker-icon.png',
  iconAnchor: [12.5, 41],
});

const highlightedMarkerIcon = L.icon({
  iconUrl: '/marker-icon-red.png',
  iconAnchor: [12.5, 41],
});

const CustomMarkerClusterGroup = ({ points, circleRadius, id }: CustomMarkerClusterGroupProps) => {
  const markersCount = useRef<Record<string, number>>({});
  const isSpiderfied = useRef(false);

  const markers = useMemo(() => {
    const output: JSX.Element[] = [];

    for (let i = points.length - 1; i >= 0; --i) {
      const point = points[i];
      const key = `${point.lat}_${point.lng}`;
      markersCount.current[key] = (markersCount.current[key] ?? 0) + 1;

      if (markersCount.current[key] > MAX_CLUSTER_CHILDREN) continue;

      if (point.isPrivate) {
        output.push(
          <Circle
            // @ts-ignore - All the unknown props are saved in the `options` property of the marker object
            isHighlighted={point.isHighlighted}
            sessionKey={point.sessionKey}
            className='max-privacy-map-circle'
            key={`${point.sessionKey}_${i}`}
            center={[point.lat, point.lng]}
            radius={circleRadius}
            {...(point.isHighlighted ? { color: '#F7761B' } : {})}
          />,
        );
      } else {
        output.push(
          <Marker
            // @ts-ignore - All the unknown props are saved in the `options` property of the marker object
            isHighlighted={point.isHighlighted}
            sessionKey={point.sessionKey}
            key={`${point.sessionKey}_${i}`}
            position={[point.lat, point.lng]}
            icon={point.isHighlighted ? highlightedMarkerIcon : markerIcon}
          >
            <Popup className='session-info-tooltip' minWidth={244} maxWidth={580} offset={[0, -20]}>
              <SessionInfoTooltip sessionKey={point.sessionKey} />
            </Popup>
          </Marker>,
        );
      }
    }

    return output;
  }, [circleRadius, points]);

  const createClusterCustomIcon = useCallback((cluster: any) => {
    const childMarkers = cluster.getAllChildMarkers() as LeafletMarker[];

    // @ts-ignore - The `isHighlighted` property was added with a prop a few lines above
    const highlightedMarker = childMarkers.find((marker) => marker.options.isHighlighted);
    if (!isSpiderfied.current && highlightedMarker) {
      delay(() => {
        cluster.spiderfy();
        highlightedMarker.fire('click');
      }, 200);

      isSpiderfied.current = true;
    }

    return L.divIcon({
      html: `<div><span>${processBigNumber(cluster.getChildCount(), checkCommaPosForCluster)}</span></div>`,
      className: 'marker-cluster marker-cluster-small',
      iconSize: L.point(40, 40, true),
    });
  }, []);

  return (
    <MarkerClusterGroup
      id={id}
      maxClusterRadius={50}
      removeOutsideVisibleBounds={true}
      iconCreateFunction={createClusterCustomIcon}
    >
      {markers}
    </MarkerClusterGroup>
  );
};

export default CustomMarkerClusterGroup;
